import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import ReactApexChart from "react-apexcharts";

const AUSTRALIA_GEO_URL =
  "https://raw.githubusercontent.com/rowanhogan/australian-states/master/states.geojson";

const stateCoordinates = {
  "New South Wales": [147.2, -32.5],
  Victoria: [144.96, -37.81],
  Queensland: [143.0, -23.5],
  "South Australia": [138.6, -34.9],
  "Western Australia": [121.9, -25.8],
  Tasmania: [147.3, -42.9],
  "Northern Territory": [133.8, -19.3],
  "Australian Capital Territory": [149.1, -35.3],
};

const normalizeStateName = (stateName) => {
  if (!stateName) return "";
  const stateMapping = {
    NSW: "New South Wales",
    "New South Wales": "New South Wales",
    VIC: "Victoria",
    Victoria: "Victoria",
    QLD: "Queensland",
    Queensland: "Queensland",
    SA: "South Australia",
    "South Australia": "South Australia",
    WA: "Western Australia",
    "Western Australia": "Western Australia",
    TAS: "Tasmania",
    Tasmania: "Tasmania",
    NT: "Northern Territory",
    "Northern Territory": "Northern Territory",
    ACT: "Australian Capital Territory",
    "Australian Capital Territory": "Australian Capital Territory",
  };
  const normalized = stateName.trim();
  if (stateMapping[normalized]) return stateMapping[normalized];
  const matchingKey = Object.keys(stateMapping).find(
    (key) => normalized.toLowerCase() === key.toLowerCase(),
  );
  return matchingKey ? stateMapping[matchingKey] : normalized;
};

const getCoordinatesForState = (stateName) => {
  if (!stateName) return null;
  if (stateCoordinates[stateName]) return stateCoordinates[stateName];
  const normalized = normalizeStateName(stateName);
  if (stateCoordinates[normalized]) return stateCoordinates[normalized];
  const matchingKey = Object.keys(stateCoordinates).find(
    (key) => key.toLowerCase() === stateName.toLowerCase(),
  );
  return matchingKey ? stateCoordinates[matchingKey] : null;
};

// ─── Best Performing Offer Card ───────────────────────────────────────────────
const BestOfferCard = ({ offer }) => {
  if (!offer) {
    return (
      <div className="rounded-xl border border-stroke bg-white p-4 h-full flex items-center justify-center">
        <p className="text-gray-400 text-sm font-poppins">No offer data yet</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-[#f0b429] bg-gradient-to-br from-[#fffbf0] to-[#fff8e0] p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[13px] font-poppins font-[600] text-[#b45309] uppercase tracking-wide">
          Best Performing Offer
        </span>
      </div>
      <p className="text-[16px] font-poppins font-[700] text-[#1c2434] mb-4 leading-tight">
        {offer.title || "—"}
      </p>
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "Views", value: offer.views, color: "#0ea5e9" },
          { label: "Clicks", value: offer.clicks, color: "#8b5cf6" },
          { label: "Redemptions", value: offer.redemptions, color: "#22c55e" },
        ].map(({ label, value, color }) => (
          <div
            key={label}
            className="rounded-lg text-center py-2 px-1"
            style={{ background: `${color}18`, border: `1px solid ${color}33` }}
          >
            <p
              className="text-[18px] font-poppins font-[700]"
              style={{ color }}
            >
              {value ?? 0}
            </p>
            <p className="text-[11px] font-poppins text-[#64748b]">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Customer Origins Card ────────────────────────────────────────────────────
const CustomerOriginsCard = ({ customerSegments, topSuburbs }) => {
  const {
    newCustomers = 0,
    repeatCustomers = 0,
    totalCustomers = 0,
  } = customerSegments || {};
  const newPct =
    totalCustomers > 0 ? Math.round((newCustomers / totalCustomers) * 100) : 0;
  const repeatPct =
    totalCustomers > 0
      ? Math.round((repeatCustomers / totalCustomers) * 100)
      : 0;

  return (
    <div className="rounded-xl border border-stroke bg-white p-4">
      <h4 className="text-[14px] font-poppins font-[600] text-[#1c2434] mb-3 flex items-center gap-2">
        Customer Origins
      </h4>

      {/* New vs Repeat */}
      <div className="mb-4">
        <div className="flex justify-between text-[12px] font-poppins text-[#64748b] mb-1">
          <span>New Customers ({newPct}%)</span>
          <span>Returning ({repeatPct}%)</span>
        </div>
        <div className="h-2 w-full rounded-full bg-[#e2e8f0] overflow-hidden flex">
          <div
            className="h-full rounded-l-full transition-all duration-500"
            style={{ width: `${newPct}%`, background: "#0ea5e9" }}
          />
          <div
            className="h-full rounded-r-full transition-all duration-500"
            style={{ width: `${repeatPct}%`, background: "#22c55e" }}
          />
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[11px] font-poppins font-[600] text-[#0ea5e9]">
            {newCustomers} new
          </span>
          <span className="text-[11px] font-poppins font-[600] text-[#22c55e]">
            {repeatCustomers} returning
          </span>
        </div>
      </div>

      {/* Top Suburbs */}
      {topSuburbs?.length > 0 && (
        <>
          <p className="text-[12px] font-poppins font-[600] text-[#334155] mb-2">
            Top Suburbs
          </p>
          <div className="space-y-1">
            {topSuburbs.slice(0, 5).map((item, i) => (
              <div key={item.suburb} className="flex items-center gap-2">
                <span className="text-[11px] font-poppins text-[#94a3b8] w-4">
                  {i + 1}.
                </span>
                <div className="flex-1 h-1.5 bg-[#e2e8f0] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#00ADEF] rounded-full"
                    style={{
                      width: `${Math.round((item.count / (topSuburbs[0]?.count || 1)) * 100)}%`,
                    }}
                  />
                </div>
                <span className="text-[11px] font-poppins text-[#475569] min-w-[70px] truncate">
                  {item.suburb}
                </span>
                <span className="text-[11px] font-poppins font-[600] text-[#1c2434]">
                  {item.count}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {(!topSuburbs || topSuburbs.length === 0) && (
        <p className="text-[12px] font-poppins text-gray-400">
          No suburb data available
        </p>
      )}
    </div>
  );
};

// ─── Time of Day Chart ────────────────────────────────────────────────────────
const TimeOfDayChart = ({ timeOfDay }) => {
  const labels = ["Morning", "Lunch", "Afternoon", "Evening"];
  const values = labels.map((l) => timeOfDay?.[l] || 0);
  const colors = ["#f59e0b", "#ef4444", "#8b5cf6", "#1e40af"];

  const options = {
    chart: {
      type: "donut",
      fontFamily: "Poppins, sans-serif",
      toolbar: { show: false },
    },
    labels,
    colors,
    legend: { position: "bottom", fontSize: "12px" },
    dataLabels: { enabled: true, formatter: (val) => `${Math.round(val)}%` },
    plotOptions: {
      pie: {
        donut: {
          size: "60%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total",
              fontSize: "13px",
              fontWeight: 600,
              color: "#1c2434",
              formatter: () => values.reduce((a, b) => a + b, 0).toString(),
            },
          },
        },
      },
    },
    tooltip: { y: { formatter: (val) => `${val} redemptions` } },
  };

  const hasData = values.some((v) => v > 0);

  return (
    <div className="rounded-xl border border-stroke bg-white p-4">
      <h4 className="text-[14px] font-poppins font-[600] text-[#1c2434] mb-1 flex items-center gap-2">
        Time of Day
      </h4>
      <p className="text-[11px] font-poppins text-[#94a3b8] mb-3">
        When customers redeem offers
      </p>
      {hasData ? (
        <ReactApexChart
          options={options}
          series={values}
          type="donut"
          height={260}
        />
      ) : (
        <div className="flex items-center justify-center h-[200px]">
          <p className="text-gray-400 text-sm font-poppins">
            No redemption data yet
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Day of Week Chart ────────────────────────────────────────────────────────
const DayOfWeekChart = ({ dayOfWeek }) => {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const values = days.map((d) => dayOfWeek?.[d] || 0);
  const maxVal = Math.max(...values, 1);

  const options = {
    chart: {
      type: "bar",
      fontFamily: "Poppins, sans-serif",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: "55%",
        distributed: true,
      },
    },
    colors: days.map((d) =>
      d === "Sat" || d === "Sun" ? "#00ADEF" : "#7dd3fc",
    ),
    dataLabels: { enabled: false },
    xaxis: {
      categories: days,
      labels: {
        style: { fontSize: "12px", fontFamily: "Poppins, sans-serif" },
      },
    },
    yaxis: {
      min: 0,
      max: Math.ceil(maxVal / 5) * 5 || 5,
      tickAmount: 5,
      labels: { style: { fontSize: "11px" } },
    },
    legend: { show: false },
    tooltip: { y: { formatter: (val) => `${val} redemptions` } },
    grid: { borderColor: "#f1f5f9" },
  };

  const hasData = values.some((v) => v > 0);

  return (
    <div className="rounded-xl border border-stroke bg-white p-4">
      <h4 className="text-[14px] font-poppins font-[600] text-[#1c2434] mb-1 flex items-center gap-2">
        Day of Week
      </h4>
      <p className="text-[11px] font-poppins text-[#94a3b8] mb-3">
        Redemption activity by day
      </p>
      {hasData ? (
        <ReactApexChart
          options={options}
          series={[{ name: "Redemptions", data: values }]}
          type="bar"
          height={230}
        />
      ) : (
        <div className="flex items-center justify-center h-[200px]">
          <p className="text-gray-400 text-sm font-poppins">
            No redemption data yet
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Order Type Toggle ────────────────────────────────────────────────────────
const OrderTypeToggle = ({ selected, onChange, orderTypeTotals }) => {
  const options = [
    {
      value: "all",
      label: "All",
      count: (orderTypeTotals?.instore || 0) + (orderTypeTotals?.online || 0),
    },
    {
      value: "instore",
      label: "In-store",
      count: orderTypeTotals?.instore || 0,
    },
    { value: "online", label: "Online", count: orderTypeTotals?.online || 0 },
  ];
  return (
    <div className="flex gap-2 flex-wrap">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1.5 rounded-full text-[12px] font-poppins font-[500] transition-all duration-200 border ${
            selected === opt.value
              ? "bg-[#00ADEF] text-white border-[#00ADEF] shadow-sm"
              : "bg-white text-[#64748b] border-[#e2e8f0] hover:border-[#00ADEF] hover:text-[#00ADEF]"
          }`}
        >
          {opt.label}
          <span className="ml-1 font-[600]">({opt.count})</span>
        </button>
      ))}
    </div>
  );
};

// ─── Main RegionMap Component ─────────────────────────────────────────────────
const RegionMap = () => {
  const { redemptionByDemographics } = useSelector(
    (state) => state?.merchantReducer,
  );

  const [tooltipContent, setTooltipContent] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [mapFilter, setMapFilter] = useState("all");

  const mapContainerRef = useRef(null);
  const tooltipRef = useRef(null);
  const mapRef = useRef(null);

  const data = redemptionByDemographics?.data;

  // Select the correct demographic dataset based on toggle
  const activeDemographics =
    mapFilter === "instore"
      ? data?.instoreDemographics
      : mapFilter === "online"
        ? data?.onlineDemographics
        : data?.demographics;

  const handleMarkerClick = (marker, event) => {
    if (activeMarker === marker.state) {
      setTooltipContent(null);
      setActiveMarker(null);
      return;
    }

    const ageGroup = marker.ageGroup || {};
    const gender = marker.gender || {};

    const content = (
      <div className="p-3 text-sm font-poppins">
        <h3 className="font-[700] text-[#1c2434] text-[13px] mb-2 border-b border-[#e2e8f0] pb-2">
          {marker.state}
        </h3>
        <p className="text-[11px] text-[#64748b] mb-1 font-[600]">
          Total Redemptions:{" "}
          <span className="text-[#1c2434]">{marker.totalRedemptions || 0}</span>
        </p>

        <p className="font-[600] text-[#334155] text-[12px] mt-2 mb-1">
          Age Groups
        </p>
        <div className="space-y-1">
          {Object.entries(ageGroup).map(([group, count]) => (
            <div key={group} className="flex justify-between text-[11px]">
              <span className="text-[#64748b]">{group}</span>
              <span className="font-[600] text-[#1c2434]">{count}</span>
            </div>
          ))}
        </div>

        <p className="font-[600] text-[#334155] text-[12px] mt-2 mb-1">
          Gender
        </p>
        <div className="space-y-1">
          {Object.entries(gender).map(([g, count]) => (
            <div key={g} className="flex justify-between text-[11px]">
              <span className="text-[#64748b] capitalize">{g}</span>
              <span className="font-[600] text-[#1c2434]">{count}</span>
            </div>
          ))}
        </div>
      </div>
    );

    const mapContainer = mapContainerRef.current?.getBoundingClientRect();
    if (!mapContainer) return;
    const mapWidth = mapRef.current
      ? mapRef.current.getBoundingClientRect().width
      : mapContainer.width;
    let newX = event.nativeEvent.offsetX + 15;
    let newY = event.nativeEvent.offsetY - 10;
    if (event.nativeEvent.offsetX > mapWidth * 0.7)
      newX = event.nativeEvent.offsetX - 215;
    if (event.nativeEvent.offsetY > mapContainer.height * 0.7)
      newY = event.nativeEvent.offsetY - 150;

    setTooltipContent(content);
    setActiveMarker(marker.state);
    setTooltipPosition({ x: newX, y: newY });
  };

  // Close tooltip when user clicks anywhere outside the tooltip box
  useEffect(() => {
    const handleDocClick = (e) => {
      // If the click target is inside the tooltip, do nothing
      if (tooltipRef.current && tooltipRef.current.contains(e.target)) return;
      setTooltipContent(null);
      setActiveMarker(null);
    };
    const handleResize = () => {
      setTooltipContent(null);
      setActiveMarker(null);
    };
    document.addEventListener("click", handleDocClick);
    window.addEventListener("resize", handleResize);
    return () => {
      document.removeEventListener("click", handleDocClick);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const hasMapData = activeDemographics?.some((s) => s.totalRedemptions > 0);

  return (
    <div className="rounded-sm border border-stroke bg-white px-5 pt-5 pb-6 shadow-default dark:border-strokedark dark:bg-boxdark summary">
      {/* Section header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <span className="text-[18px] sm:text-[20px] font-poppins font-[600] text-[#1c2434]">
          User Demographics by Redemption Location
        </span>
      </div>

      {/* Main grid: Map (left) + Cards (right) with equal width (50% / 50% split) */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-5">
        {/* Map Panel */}
        <div className="rounded-xl border border-stroke bg-white p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
            <h4 className="text-[14px] font-poppins font-[600] text-[#1c2434]">
              Redemption Locations
            </h4>
            <OrderTypeToggle
              selected={mapFilter}
              onChange={setMapFilter}
              orderTypeTotals={data?.orderTypeTotals}
            />
          </div>
          <div
            ref={mapContainerRef}
            className="relative"
            style={{ height: "340px" }}
          >
            <div
              ref={mapRef}
              style={{ width: "100%", height: "100%", position: "relative" }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <ComposableMap
                projection="geoMercator"
                projectionConfig={{ scale: 900, center: [133.7751, -25.2744] }}
                style={{ width: "100%", height: "100%" }}
              >
                <Geographies geography={AUSTRALIA_GEO_URL}>
                  {({ geographies }) =>
                    Array.isArray(geographies) && geographies.map((geo) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill="#e8f4f8"
                        stroke="#FFFFFF"
                        strokeWidth={0.5}
                        style={{
                          default: { fill: "#e8f4f8", outline: "none" },
                          hover: { fill: "#b3dff0", outline: "none" },
                        }}
                      />
                    ))
                  }
                </Geographies>

                {Array.isArray(activeDemographics) && activeDemographics.map((marker) => {
                  const coordinates = getCoordinatesForState(marker.state);
                  if (!coordinates || !marker.totalRedemptions) return null;
                  const isActive = activeMarker === marker.state;
                  const radius = Math.min(
                    4 + Math.sqrt(marker.totalRedemptions) * 2,
                    16,
                  );
                  return (
                    <Marker
                      key={marker.state}
                      coordinates={coordinates}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMarkerClick(marker, event);
                      }}
                    >
                      <circle
                        r={radius}
                        fill={isActive ? "#0077b6" : "#00ADEF"}
                        fillOpacity={0.85}
                        stroke="#fff"
                        strokeWidth={2}
                        style={{ cursor: "pointer" }}
                      />
                    </Marker>
                  );
                })}
              </ComposableMap>

              {tooltipContent && (
                <div
                  ref={tooltipRef}
                  className="absolute bg-white rounded-xl shadow-lg border border-gray-200 z-10 min-w-[160px]"
                  style={{
                    left: `${tooltipPosition.x}px`,
                    top: `${tooltipPosition.y}px`,
                    maxWidth: "220px",
                    transition: "left 0.15s, top 0.15s",
                  }}
                >
                  {tooltipContent}
                </div>
              )}

              {!hasMapData && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-xl">
                  <p className="text-gray-400 font-poppins text-sm">
                    No redemption data available
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right side cards */}
        <div className="flex flex-col gap-4">
          <BestOfferCard offer={data?.bestPerformingOffer} />
          <CustomerOriginsCard
            customerSegments={data?.customerSegments}
            topSuburbs={data?.topSuburbs}
          />
        </div>
      </div>

      {/* Bottom row: Time of Day + Day of Week */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <TimeOfDayChart timeOfDay={data?.timeOfDay} />
        <DayOfWeekChart dayOfWeek={data?.dayOfWeek} />
      </div>
    </div>
  );
};

export default RegionMap;
